
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
	MultiStepSportEvent,
	Platform,
	SportEventAggregation,
	SportEventStatus,
	SportType,
} from '@/@types/graphql-types';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { LocaleResponse } from '@/helpers/locale-helper';
import { UpcomingEvent } from '@/interfaces/sport-graphql';

import {
	GetMultiStepSportOverviewDocument,
	GetMultiStepSportOverviewQueryVariables as MultiStepSportVariables,
} from '@/pages/graphql/queries/GetMultiStepSportOverview.query';

import type { WebLocale } from '@/enums/web-locale';

type TimeGroup = 'live' | 'today' | 'upcoming';

const Language = namespace('language');

// depending on where the JS machine is, this can be different.
// so we force it by comparing it to the timezone of the current webLocale.
const changeTimezone = (timeZone: string, targetDate = new Date()) => {
	// we can hard code en-US because in the end we're only interested in the Date object
	const localisedDateString = targetDate.toLocaleDateString('en-US', { timeZone, timeZoneName: 'short' });
	return new Date(localisedDateString);
};

const isToday = (today: Date, targetDate: Date) => {
	return today.toDateString() === targetDate.toDateString();
};

@Component({
	name: 'ProvideUpcomingEventData',
	apollo: {
		upcomingEvents: {
			query: GetMultiStepSportOverviewDocument,
			variables(): MultiStepSportVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.sportEventBucketsV2.edges
					.map((edge: any) => edge.node.edges)
					.flat()
					.map((edge: any) => edge.node);
			},
			skip() {
				return !this.filterByParam || !(this.$route.meta!.data as string)?.includes('multistep');
				// return this.competitionIds.length !== 0 && this.competitionIds[0] === undefined;
			},
		},
	},
})
export default class ProvideUpcomingEventData extends Vue {
	upcomingEvents: MultiStepSportEvent[] = [];

	@Prop() sportType: SportType;
	@Prop() filterBy: 'sport' | 'competition';
	@Prop() filterByParam: SportType | string;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter country: string;
	@Language.Getter localeObject: LocaleResponse;

	get eventTimeGroup(): 'live' | 'today' | 'upcoming' {
		if (this.events.live.length > 0) {
			return 'live';
		} else if (this.events.today.length > 0) {
			return 'today';
		} else {
			return 'upcoming';
		}
	}

	get queryVariables(): MultiStepSportVariables {
		const localeTodayStart = shiftDateWithTimezone(this.localeObject.timezone);
		return {
			platform: Platform.Web,
			language: this.language,
			country: this.country,
			first: ['sport', 'competition'].includes(this.filterBy) ? 1 : 10,
			bucketSize: this.filterBy === 'sport' ? 10 : 10, // @todo check if we want to keep it for multi sport queries
			groupBy: SportEventAggregation.Sport,
			filter: {
				startAt: {
					from: localeTodayStart.toISOString(),
					to: null,
				},
				competitionIds: this.filterBy === 'competition' ? [this.filterByParam] : [],
				sports: this.filterBy === 'sport' ? [this.filterByParam as SportType] : [],
			},
			timezoneOffset: 0,
		};
	}

	get isDataLoading() {
		return this.$apollo.queries.upcomingEvents.loading || !this.filterByParam;
	}

	get events(): Record<TimeGroup, UpcomingEvent[]> {
		const allEvents = this.upcomingEvents.map(event => {
			const lineItems = this.getLineItems(event);
			const competitionStageText = this.getCompetitionStageText(event);
			const localStartAt = changeTimezone(this.localeObject.timezone, new Date(event.content.startAt)); // needs to be put in current webLcoale's timezone
			const startAt = new Date(event.content.startAt);

			return {
				id: event.id,
				lineItems,
				fullPath: event.parent?.content.fullPath,
				startAt: startAt,
				localStartAt: localStartAt,
				sport: event.sport,
				competition: {
					text: event.competition?.content?.name,
					fullPath: event.competition?.content?.fullPath,
				},
				competitionStageText,
				offers: event?.offers,
				watchNowOffer: event.watchNowOffer,
				mysportlistEntry: event.mysportlistEntry,
				sportEvent: event, // used for snowplow context and tracking
			} as UpcomingEvent;
		});
		const localeToday = changeTimezone(this.localeObject.timezone);
		const liveMatches = allEvents.filter(event => event?.sportEvent?.content?.status === SportEventStatus.Live);
		const todayMatches = allEvents.filter(event => isToday(localeToday, event.localStartAt));
		const upcomingMatches = allEvents.filter(event => !isToday(localeToday, event.localStartAt));

		return {
			live: liveMatches,
			today: todayMatches,
			upcoming: upcomingMatches,
		};
	}

	// depending on sport, it can the team's name or the course of the event.
	// that's why it's called line items.
	getLineItems(event: MultiStepSportEvent): UpcomingEvent['lineItems'] {
		// MultiStepSportEvent
		return [{ text: event.parent?.content.name ?? '' }];
	}

	getCompetitionStageText(event: MultiStepSportEvent) {
		const { name, number } = event.content?.competitionStage || {};

		// capitalise racing (e.g. Practice, Qualifying, ...)
		const formulaRacingName = `${name?.slice(0, 1).toUpperCase()}${name?.slice(1).toLowerCase()}`;
		return [formulaRacingName, number].filter(item => item !== null).join(' ');
	}

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({
				events: this.events,
				eventTimeGroup: this.eventTimeGroup,
				isDataLoading: this.isDataLoading,
			});
		}

		return this.$slots[0];
	}
}
